import 'tooltipster/dist/css/tooltipster.bundle.css';

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Expired from 'components/Expired';
import InvalidSession from 'components/InvalidSession';
import RangeQuestion from 'components/RangeQuestion';
import SingleSelect from 'components/SingleSelect';
import StartRatingQuestion from 'components/StartRating';
import ThankYou from 'components/ThankYou';
import { Tooltipster } from 'react-tooltipster';
import _ from 'lodash';
import classNames from 'classnames';
import loader from 'assets/images/loader.gif';
import platform from 'platform';
import useFetch from 'use-http';
import useProgressProvider from 'providers/ProgressProvider';
import useSessionProvider from 'providers/SessionProvider';
import useSpeechToText from 'components/SpeechRecongnization/useSpeechRecognization';
import useSurveyProvider from 'providers/SurveyProvider';
import { useTranslation } from 'react-i18next';
import BotIcon from 'components/BotIcon';

export type ClientResponseItem = {
    question_type: string;
    clientResponse: string[];
    skip: boolean;
    session_id: string;
    question_id: string;
};

export type Checkbox = {
    questionId: string;
    label: string;
    value: string;
};

export type CheckboxOption = {
    label: string;
    value: string;
};

const cloudSpeechApiKey = 'AIzaSyAkUDloJwxS5jouGnY43tcQ2VaArZQcyRM';

export default function Dashboard() {
    const [currText, setcurrText] = useState<string>('');
    const [currQuestionId, SetCurrQuestionId] = useState<string>('');
    const [validationText, setValidationText] = useState<string>('');
    const [checkbox, setCheckbox] = useState<Checkbox[]>([]);
    const [answerMap, setAnswerMap] = useState<{ questionId: string; text: string }[]>([]);

    const textInput = useRef(null);
    const { convo, setConvo, setRemainingTime, loading, setLoading } = useSurveyProvider();
    const messagesEndRef = useRef(null);
    const { status, survey, setStatus, sessionId, botIconPath, showSkip, connection, total_question } =
        useSessionProvider();
    const { progress, setProgress, showProgress, setDone, done, total, setTotal } = useProgressProvider();
    const { post } = useFetch();
    const { post: postfeedback } = useFetch(process.env.REACT_APP_API_URL);
    const {
        startSpeechToText,
        stopSpeechToText,
        isRecording,
        results
        // blob,
    } = useSpeechToText({
        continuous: false,
        crossBrowser: true,
        googleApiKey: cloudSpeechApiKey,
        onStartSpeaking: () => { },
        onStoppedSpeaking: () => { },
        timeout: 10000
    });
    const { t, i18n } = useTranslation();

    const [clientResponseItems, setClientResponseItems] = useState<ClientResponseItem[]>([]);

    const transformConvoToClientResponseItems = (convo: any[]): ClientResponseItem[] => {
        if (!convo || convo.length === 0) return [];

        const candidateQuestions = convo.filter((question) => {
            const { question_type } = question;

            const isCommentOrCheckbox = question_type === 'Comment List' || question_type === 'Checkbox List';
            const isNotSubmit = question.question_name !== 'Survey_Ending';
            return isCommentOrCheckbox && isNotSubmit;
        });

        const getAnswers = (question: any) => {
            const { answer } = question;

            if (Array.isArray(answer)) {
                return answer.map((answer) => {
                    const { label } = answer;

                    if (typeof answer === 'object' && label) return label;
                    else return answer;
                });
            } else return [];
        };

        const getClientResponseByQuestionId = (questionId: string) => {
            return (
                clientResponseItems.find((responseItem: ClientResponseItem) => responseItem.question_id === questionId)
                    ?.clientResponse ?? []
            );
        };

        return candidateQuestions.map((question) => {
            const { skip, question_type, question_id } = question;

            const clientResponse = getAnswers(question);

            return {
                question_type,
                clientResponse: clientResponse.length > 0 ? clientResponse : getClientResponseByQuestionId(question_id),
                skip,
                session_id: sessionId,
                question_id
            };
        });
    };

    useEffect(() => {
        if (results && results.length > 0) {
            let event: any = {};
            event.target = {};
            event.target.value = survey.staticSurvey ? results[0] : results[results.length - 1];
            if (survey.staticSurvey) {
                setcurrText(results[0]);
            } else {
                setcurrText(results[results.length - 1]);
            }
        }
    }, [results]);

    useEffect(() => {
        if (!convo || convo.length === 0) return;

        const checkboxOptions = convo
            .filter((x) => x.question_type === 'Checkbox List')
            .flatMap((x) =>
                x.answer.map((y) => {
                    const optionValue = x.options.find((z) => z.label === y)?.value;
                    if (optionValue !== undefined && optionValue !== null) {
                        return {
                            questionId: x.question_id,
                            label: y,
                            value: optionValue
                        };
                    }
                })
            )
            .filter((option) => option !== undefined);

        setCheckbox(checkboxOptions);
    }, [convo]);

    useEffect(() => {
        if (messagesEndRef && messagesEndRef.current) {
            if (!survey.staticSurvey) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            setcurrText('');
            if (textInput.current && !survey.staticSurvey) {
                textInput.current.focus();
            }
        }
    }, [convo, i18n, loading]);

    useEffect(() => {
        document.body.classList.add('body--survey');
        return () => document.body.classList.remove('body--survey');
    }, []);

    useEffect(() => {
        if (convo) {
            if (survey.staticSurvey) {
                const clientResponseItemsFromConvo = transformConvoToClientResponseItems(convo);
                setClientResponseItems(clientResponseItemsFromConvo);
            }

            setAnswerMap(
                convo.map((x) => {
                    if (x.question_type === 'Comment List') {
                        setcurrText(x.answer[0]);
                        return {
                            questionId: x.question_id,
                            text: x.answer[0]
                        };
                    }
                })
            );

            // Update checkbox state
            const updatedCheckbox: Checkbox[] = [];

            convo.forEach((question: any) => {
                if (question.question_type !== 'Checkbox List') return;

                const questionId = question.question_id;

                const clientResponseItem = clientResponseItems.find((x) => x.question_id === questionId);
                if (!clientResponseItem) return;

                const convoCheckbox = clientResponseItem.clientResponse;
                if (!convoCheckbox || convoCheckbox.length === 0) return;

                const updatedCheckboxItem = convoCheckbox.map((label) => {
                    return {
                        questionId,
                        label,
                        value: question.options.find((option: CheckboxOption) => option.label === label)?.value
                    };
                });

                updatedCheckbox.push(...updatedCheckboxItem);
                setCheckbox(updatedCheckbox);
            });
        }
    }, [convo]);

    const onEnterPress = (e: any, questionId) => {
        const currTextEval = !survey.staticSurvey ? currText : answerMap[questionId]?.text;
        if (e.keyCode === 13) {
            if (currTextEval && currTextEval.trim() !== '' && !e.shiftKey) {
                HandleResponse(
                    [currTextEval],
                    -1,
                    !survey.staticSurvey
                        ? convo[convo.length - 1]
                        : convo.findIndex((x) => x.question_id === questionId),
                    false,
                    false,
                    questionId
                );
            } else {
            }
        }
    };

    const getQuestionTypeById = (questionId: string) => {
        const question = convo.find((x) => x.question_id === questionId);
        return question?.question_type;
    };

    const updateClientResponseList = (questionId: string, response: string[]) => {
        const updatedClientResponseItems = clientResponseItems.map((clientResponseItem) => {
            return clientResponseItem.question_id === questionId
                ? { ...clientResponseItem, clientResponse: [...new Set(response)] }
                : clientResponseItem;
        });

        setClientResponseItems(updatedClientResponseItems);
    };

    const handleChange = (e: any, questionId: string = '') => {
        setcurrText(e.target.value);
        if (survey.staticSurvey) {
            const updatedMap = answerMap.map((x) => {
                if (x?.questionId === questionId) {
                    return { ...x, text: e.target.value };
                } else {
                    return x;
                }
            });
            setAnswerMap(updatedMap);
        }
    };

    const preHandleResponse = async () => {
        return new Promise<void>(async (resolve, reject) => {
            try {
                const handleResponsePromises = clientResponseItems.filter(responseItem => responseItem.clientResponse.length > 0).map(async (responseItem) => {
                    const item = convo[convo.findIndex((x) => x.question_id === responseItem.question_id)];
                    return HandleResponse(
                        responseItem.clientResponse,
                        -1,
                        item,
                        responseItem.skip,
                        false,
                        responseItem.question_id
                    );
                });

                await Promise.all(handleResponsePromises);
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };

    const HandleResponse = async (
        e: any,
        optionIndex,
        item: any,
        skip: boolean = false,
        undo: boolean = false,
        questionId: any = ''
    ) => {
        try {
            setLoading(true);

            if (!undo) {
                let message = {
                    text: !skip ? (item.question_type === 'Comment List' ? e : e.map((x) => x.label)) : [t('SKIP')]
                };

                if (survey.staticSurvey) {
                    if (item.question_type === 'Checkbox List' && item.question_name !== 'Survey_Ending') {
                        message.text = e;
                    }
                }

                let updatedChat = [...convo, message];
                // var prevCollection = convo.slice(0, convo.findIndex((x) => x.question_id === item.question_id) + 1);
                // var prevCollection2 = convo.slice(convo.findIndex((x) => x.question_id === item.question_id) + 1);

                // updatedChat = [...prevCollection, message, ...prevCollection2];
                setConvo([...updatedChat]);
                //setConvo([convo]);
                // Spasning  /// english  => question index  -> translation

                if (survey.staticSurvey) {
                    if (item.question_name === 'Survey_Ending') {
                        try {
                            await preHandleResponse();
                        } catch (error) {
                            console.error('Error during preHandleResponse:', error);
                        }
                    }
                }
                if (item.question_name === 'System_Lang_Selection') {
                    var languageCode = survey.languages?.find((x) => x.label.trim() === e[0].label.trim())?.value;
                    if (languageCode) {
                        i18n.changeLanguage(languageCode);
                    }
                }
                try {
                    const res = await post('/Session/AddResponse', {
                        // clientResponse: skip ? [] : message.text,
                        clientResponse: skip
                            ? []
                            : clientResponseItems.find((x) => x.question_id === questionId)?.clientResponse ?? message.text,
                        skip,
                        session_id: sessionId.toString(),
                        question_id: questionId.toString() === '' ? item.question_id.toString() : questionId.toString()
                    })
                    const status = res?.status?.split(' ').join('');
                    if (Object.keys(res).indexOf('validation_text') > -1 && res.validation_text) {
                        if (survey.staticSurvey && res.validation_text) {
                            toast.error(res.validation_text);
                            setValidationText(res.validation_text);
                        } else if (!survey.staticSurvey) {
                            setValidationText('');
                        } else {
                            setValidationText('');
                        }
                        var serverMessage = res?.chat[res?.chat.length - 1];
                        serverMessage.text = [res?.validation_text, ...serverMessage.text];
                        setLoading(false);
                    } else {
                        if (status === 'SurveyExpired') {
                            setStatus('Expired');
                            connection.invoke('AddResponse', sessionId, [], [], [], 0, 'SurveyExpired', []);
                            setLoading(false);
                        } else if (status === 'SurveyCompleted' || status === 'SurveyPartiallyCompleted') {
                            setStatus('Completed');
                            setProgress(100);
                            setRemainingTime(0);
                            let browserInfo = {
                                sessionId: sessionId.toString(),
                                browserName: platform?.name,
                                browserVersion: platform?.version,
                                browserLayout: platform?.layout,
                                os: `${platform?.os?.family} ${platform?.os?.version}`,
                                browserDescription: platform?.description,
                                isMobile:
                                    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                                        navigator?.userAgent
                                    ) === true
                                        ? 1
                                        : 0
                            };
                            setStatus('Completed');

                            postfeedback('/SurveyBot/SaveBrowserInfo', browserInfo).then((res) => {
                                console.log('Save browser info response: ', res);
                            });
                            connection.invoke('AddResponse', sessionId, [], [], [], 0, 'SurveyCompleted', []);
                            setLoading(false);
                        } else if (
                            status === 'SurveyOpened' ||
                            status === 'SurveyViewed' ||
                            status === 'SurveyInProgress' ||
                            status === 'InvitationsDelivered' ||
                            status === 'InvitationsReminderSent' ||
                            status === 'InvitationsSent' ||
                            status === 'InteractionsProcessed'
                        ) {
                            setCheckbox([]);
                            setConvo(res?.chat);
                            setcurrText('');
                            setLoading(false);
                            setProgress(res?.progress);
                            setRemainingTime(res?.remaining_time);
                            setTotal(res?.total_questions);
                            setDone(res?.question_completed);
                            setValidationText('');
                        }
                    }
                } catch (ex) {
                    console.log({ ex })
                } finally {
                    setLoading(false)
                }

            } else {
                setLoading(true);
                const chat = [...convo];
                chat.splice(-1);
                chat.splice(-1);
                const lastServerMessage = chat[chat.length - 1];
                lastServerMessage.answer = [];
                let clientMessages = chat.filter((x) => x.message_type === 'Client');
                setConvo(chat);
                post('/Session/Undo', { session_id: sessionId })
                    .then((res) => {
                        setProgress(res?.progress);
                        setRemainingTime(res?.time_remaining);
                        setTotal(res?.total_questions);
                        setConvo(res?.chat);
                        setDone(res?.question_completed);
                        setTimeout(() => {
                            setLoading(false);
                        }, 1000);
                    })
                    .catch((err) => {
                        console.log({ err });
                    });
            }
        } catch (ex) {
            console.trace(ex);
        }
    };
    const renderHTML = convo?.map((item: any, index: number) => {
        const clientMessages = convo.filter((x) => x.message_type?.toLowerCase() === 'client');
        const serverMessages = convo.filter((x) => x.message_type?.toLowerCase() === 'server');
        return item?.message_type?.toLowerCase() === 'server'
            ? item.text.map((text, textIndex) => {
                return (
                    <div
                        key={`${index}-${textIndex}`}
                        className={classNames('chat left-chat', {
                            'with--skip':
                                showSkip &&
                                serverMessages &&
                                serverMessages.length > 0 &&
                                !survey?.staticSurvey &&
                                serverMessages[serverMessages.length - 1].question_id === item.question_id
                        })}>
                        <div className={'chat-img' + (textIndex !== 0 ? ' no--image' : '')}>
                            {!botIconPath && <BotIcon />}
                            {botIconPath && <img src={botIconPath} className="logo" alt="" />}
                        </div>
                        <div
                            className={classNames('chat-bubble', {
                                'with--rating': item.type === 'Rating'
                            })}
                            message-id={item.message_id}>
                            <div className="chat-text">
                                <div className="chat-question">
                                    <p>{text}</p>
                                </div>
                            </div>
                            {item.question_type === 'Comment List' && (
                                <div className="chat-answer">
                                    <div className="chat-form">
                                        <div className="chat-field">
                                            {/* <div
                              className={classNames(`comment-mic`, {
                                 'mic--active': isRecording, disabled:
                                    (convo && convo.length > 0 && convo[convo.length - 1]?.button?.length > 0) ||
                                    loading || (convo && convo.length > 0 && convo[convo.length - 1]?.rating && convo[convo.length - 1]?.rating.length > 0) ||
                                    (convo && convo.length > 0 && convo[convo.length - 1]?.multi_button && convo[convo.length - 1]?.multi_button.length > 0)
                              })}
                              onClick={() => {
                                 if (isRecording) {
                                    stopSpeechToText();
                                    if (results && results.length > 0) {
                                       let event: any = {};
                                       event.target = {};
                                       event.target.value = results[results.length - 1];
                                       setcurrText(results[results.length - 1]);
                                    }
                                 } else {
                                    startSpeechToText();
                                 }
                              }}
                           >
                              <Tooltipster
                                 className="sentiment-link"
                                 side="top"
                                 theme="tooltipster-light"
                                 content={<p style={{ whiteSpace: 'pre-line' }}>{t("RECORD")}</p>}
                              >
                                 <div className="animation-outer"></div>
                                 <i className=""></i>
                              </Tooltipster>
                           </div> */}
                                            {textIndex === item.text.length - 1 && (
                                                <>
                                                    <input
                                                        id={item.question_id}
                                                        onKeyUp={(e) => onEnterPress(e, item.question_id)}
                                                        onChange={(e) => {
                                                            SetCurrQuestionId(item.question_id);
                                                            handleChange(e, item.question_id);
                                                            if (survey.staticSurvey) {
                                                                updateClientResponseList(item.question_id, [
                                                                    e.target.value
                                                                ]);
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key !== 'Enter') return;

                                                            return (
                                                                survey.staticSurvey &&
                                                                HandleResponse(
                                                                    [currText],
                                                                    -1,
                                                                    convo[
                                                                    convo.findIndex(
                                                                        (x) => x.question_id === item.question_id
                                                                    )
                                                                    ],
                                                                    false,
                                                                    false,
                                                                    item.question_id
                                                                )
                                                            );
                                                        }}
                                                        maxLength={2000}
                                                        ref={textInput}
                                                        value={
                                                            //   !survey.staticSurvey
                                                            //       ? currText
                                                            //       :
                                                            answerMap.find((x) => x?.questionId === item?.question_id)
                                                                ?.text
                                                        }
                                                        //disabled={disabled}
                                                        placeholder={
                                                            survey.inputPlaceholder
                                                                ? survey.inputPlaceholder[i18n.language]
                                                                : t('PLACEHOLDER')
                                                        }
                                                    />
                                                    <button
                                                        onClick={(e) => {
                                                            if (survey.staticSurvey) {
                                                                updateClientResponseList(item.question_id, [
                                                                    currText
                                                                ]);
                                                            }

                                                            return HandleResponse(
                                                                [currText],
                                                                -1,
                                                                survey.staticSurvey
                                                                    ? convo[
                                                                    convo.findIndex(
                                                                        (x) =>
                                                                            x.question_id === item.question_id
                                                                    )
                                                                    ]
                                                                    : convo[convo.length - 1],
                                                                false,
                                                                false,
                                                                item.question_id
                                                            );
                                                        }}
                                                        //disabled={disabled || currText.trim() === ''}
                                                        className={
                                                            'btn btn--send ' //+
                                                            //(disabled || currText.trim() === '' ? 'btn--disable' : 'active')
                                                        }
                                                        disabled={
                                                            !currText ||
                                                            currText?.trim()?.length === 0 ||
                                                            currQuestionId !== item.question_id
                                                        }>
                                                        <Tooltipster
                                                            className="sentiment-link"
                                                            side="top"
                                                            theme="tooltipster-light"
                                                            content={
                                                                <p style={{ whiteSpace: 'pre-line' }}>{t('SEND')}</p>
                                                            }>
                                                            <i className="far fa-chevron-right"></i>
                                                        </Tooltipster>
                                                    </button>
                                                    {survey.staticSurvey &&
                                                        validationText &&
                                                        !item.can_skip &&
                                                        item.answer.length === 0 && (
                                                            <div className="validation-message">
                                                                <i className="fa-sharp fa-regular fa-circle-info"></i>
                                                                {item.validation_text}
                                                            </div>
                                                        )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {item.question_type === 'Radio List' &&
                                (textIndex === item.text.length - 1) && (
                                    <div className="chat-answer">
                                        <SingleSelect
                                            {...{
                                                item,
                                                index,
                                                HandleResponse,
                                                disabled: survey.staticSurvey
                                                    ? false
                                                    : index !== convo.length - 1 || loading
                                            }}
                                        />
                                        {survey.staticSurvey &&
                                            validationText &&
                                            !item.can_skip &&
                                            item.answer.length === 0 &&
                                            item.question_name !== 'Survey_Ending' && (
                                                <div className="validation-message">
                                                    <i className="fa-sharp fa-regular fa-circle-info"></i>
                                                    {item.validation_text}
                                                </div>
                                            )}
                                    </div>
                                )}

                            {item.question_type === 'Rating' &&
                                (textIndex === item.text.length - 1) && (
                                    <div className="chat-answer">
                                        <RangeQuestion
                                            disabled={
                                                (survey.staticSurvey
                                                    ? index !==
                                                    convo.findIndex((x) => x.question_id === item.question_id)
                                                    : index !== convo.length - 1) || loading
                                            }
                                            labels={item.options}
                                            question_id={item.question_id}
                                            answer={() => {
                                                return item.answer && item.answer.length > 0
                                                    ? item.answer[0]?.split('~').join('')
                                                    : '';
                                            }}
                                            iconNegative={item.iconNegative}
                                            iconPositive={item.iconPositive}
                                            positiveText={item.positiveText}
                                            negativeText={item.negativeText}
                                            useIcon={item.useIcon}
                                            onChange={(e) => {
                                                HandleResponse([e], -1, item, false, false, item.question_id);

                                                //   if (survey.staticSurvey) {
                                                //       setConvo(convo);
                                                //   }
                                            }}
                                        />
                                        {survey.staticSurvey &&
                                            validationText &&
                                            !item.can_skip &&
                                            item.answer.length === 0 && (
                                                <div className="validation-message">
                                                    <i className="fa-sharp fa-regular fa-circle-info"></i>
                                                    {item.validation_text}
                                                </div>
                                            )}
                                    </div>
                                )}
                            {item.question_type === 'Checkbox List' &&
                                (textIndex === item.text.length - 1) && (
                                    <div className="chat-answer">
                                        <ul className="checkbox-list">
                                            {item.options.map((it: any, buttonIndex: number) => {
                                                return (
                                                    <li key={buttonIndex.toString()}>
                                                        <input
                                                            type={'checkbox'}
                                                            name={`answer-${it?.value}-${buttonIndex}`}
                                                            id={`answer-${it?.value}-${buttonIndex}`}
                                                            className="rating__field"
                                                            disabled={
                                                                (survey.staticSurvey
                                                                    ? index !==
                                                                    convo.findIndex(
                                                                        (x) => x.question_id === item.question_id
                                                                    )
                                                                    : index !== convo.length - 1) || loading
                                                            }
                                                            checked={
                                                                checkbox
                                                                    .filter((x) => x.questionId === item.question_id)
                                                                    .map((x) => x.value)
                                                                    ?.includes(it?.value)
                                                                //   checkbox.map((x) => x.value)?.includes(it?.value) ||
                                                                //   checkbox.map((x) => x.value)?.includes(it?.value) ||
                                                                //   (survey.staticSurvey && survey
                                                                //       ? item?.answer?.includes(it?.label)
                                                                //       : item?.answer?.includes(it?.label))
                                                            }
                                                            readOnly
                                                            onChange={(e) => {
                                                                SetCurrQuestionId(item.question_id);
                                                                if (it.value.includes('none')) {
                                                                    setCheckbox((prev) => {
                                                                        const filteredPrev = prev.filter(
                                                                            (x) => x.questionId !== item.question_id
                                                                        );
                                                                        const updatedCheckbox = !filteredPrev.some(
                                                                            (x) => x.value === it.value
                                                                        )
                                                                            ? [
                                                                                {
                                                                                    questionId: item.question_id,
                                                                                    label: it?.label,
                                                                                    value: it?.value
                                                                                }
                                                                            ]
                                                                            : filteredPrev;
                                                                        if (survey.staticSurvey) {
                                                                            updateClientResponseList(
                                                                                item.question_id,
                                                                                updatedCheckbox.map((x) => x.label)
                                                                            );
                                                                        }

                                                                        return updatedCheckbox;
                                                                    });
                                                                } else {
                                                                    const otherQuestionCheckbox = checkbox.filter(
                                                                        (x) =>
                                                                            x.questionId !== item.question_id &&
                                                                            x.value !== undefined
                                                                    );

                                                                    setCheckbox((prev) => {
                                                                        let filteredPrev = prev.filter(
                                                                            (x) =>
                                                                                x.questionId === item.question_id &&
                                                                                x.value !== undefined &&
                                                                                !x.value?.includes('none')
                                                                        );

                                                                        if (
                                                                            filteredPrev
                                                                                .map((x) => x.value)
                                                                                .includes(it?.value)
                                                                        ) {
                                                                            filteredPrev = filteredPrev.filter(
                                                                                (x) => x.value !== it?.value
                                                                            );
                                                                        } else {
                                                                            filteredPrev.push({
                                                                                questionId: item.question_id,
                                                                                label: it?.label,
                                                                                value: it?.value
                                                                            });
                                                                        }

                                                                        if (survey.staticSurvey) {
                                                                            updateClientResponseList(
                                                                                item.question_id,
                                                                                [...filteredPrev].map((x) => x.label)
                                                                            );
                                                                        }

                                                                        return [
                                                                            ...otherQuestionCheckbox,
                                                                            ...filteredPrev
                                                                        ];
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                        <label
                                                            className="rating__label"
                                                            htmlFor={`answer-${it?.value}-${buttonIndex}`}>
                                                            <span className="placeholder">{it?.label}</span>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        <button
                                            onClick={() => {
                                                if (survey.staticSurvey) {
                                                    updateClientResponseList(
                                                        item.question_id,
                                                        checkbox
                                                            .filter((x) => x.questionId === item.question_id)
                                                            .map((x) => x.label)
                                                    );
                                                }

                                                HandleResponse(
                                                    checkbox.filter((x) => x.questionId === item.question_id),
                                                    -1,
                                                    survey.staticSurvey
                                                        ? convo[
                                                        convo.findIndex(
                                                            (x) => x.question_id === item.question_id
                                                        )
                                                        ]
                                                        : convo[convo.length - 1],
                                                    false,
                                                    false,
                                                    item.question_id
                                                );
                                            }}
                                            disabled={
                                                (survey.staticSurvey
                                                    ? index !==
                                                    convo.findIndex((x) => x.question_id === currQuestionId)
                                                    : index !== convo.length - 1) ||
                                                checkbox.length === 0 ||
                                                checkbox.findIndex((x) => x.questionId === item.question_id) === -1 ||
                                                loading
                                            }
                                            className={
                                                'btn btn--check' +
                                                (checkbox && checkbox.length > 0 && index === convo.length - 1
                                                    ? 'active'
                                                    : 'btn--disable')
                                            }>
                                            <i className="far fa-chevron-right"></i>
                                        </button>

                                        {survey.staticSurvey &&
                                            validationText &&
                                            !item.can_skip &&
                                            item.answer.length === 0 && (
                                                <div className="validation-message">
                                                    <i className="fa-sharp fa-regular fa-circle-info"></i>{' '}
                                                    {item.validation_text}
                                                </div>
                                            )}
                                    </div>
                                )}
                            {item.question_type === 'Star Rating' &&
                                (textIndex === item.text.length - 1) && (
                                    <div className="chat-answer">
                                        <StartRatingQuestion
                                            disabled={
                                                (survey.staticSurvey
                                                    ? index !==
                                                    convo.findIndex((x) => x.question_id === item.question_id)
                                                    : index !== convo.length - 1) || loading
                                            }
                                            question_id={item.question_id}
                                            answer={
                                                item.answer && item.answer.length > 0
                                                    ? item.answer[0] !== 'N/A'
                                                        ? parseInt(item.answer[0]?.split('~').join(''))
                                                        : item?.answer[0]
                                                    : -1
                                            }
                                            onChange={(e) => {
                                                if (survey.staticSurvey || index === convo.length - 1) {
                                                    HandleResponse([e], -1, item, false, false, item.question_id);
                                                }
                                            }}
                                            iconNegative={item.iconNegative}
                                            iconPositive={item.iconPositive}
                                            positiveText={item.positiveText}
                                            negativeText={item.negativeText}
                                            useIcon={item.useIcon}
                                            numOfStars={item.numOfStars}
                                            withNA={item.withNA}
                                        />
                                        {survey.staticSurvey &&
                                            validationText &&
                                            !item.can_skip &&
                                            item.answer.length === 0 && (
                                                <div className="validation-message">
                                                    <i className="fa-sharp fa-regular fa-circle-info"></i>
                                                    {item.validation_text}
                                                </div>
                                            )}
                                    </div>
                                )}
                            {serverMessages &&
                                serverMessages.length > 0 &&
                                serverMessages[serverMessages.length - 1].question_id === item.question_id &&
                                textIndex === item.text.length - 1 &&
                                item.can_skip && (
                                    <button
                                        type="button"
                                        disabled={loading}
                                        className={classNames('pills-btn pill--skip')}
                                        onClick={() => {
                                            HandleResponse(
                                                [],
                                                -1,
                                                survey.staticSurvey
                                                    ? convo[
                                                    convo.findIndex((x) => x.question_id === item.question_id)
                                                    ]
                                                    : convo[convo.length - 1],
                                                true,
                                                false,
                                                item.question_id
                                            );
                                        }}>
                                        <Tooltipster
                                            className="sentiment-link"
                                            side="top"
                                            theme="tooltipster-light"
                                            content={
                                                !survey.staticSurvey && (
                                                    <p style={{ whiteSpace: 'pre-line' }}>{t('SKIP')}</p>
                                                )
                                            }></Tooltipster>
                                        Skip <i className="fal fa-arrow-right-long-to-line"></i>
                                    </button>
                                )}
                        </div>
                    </div>
                );
            })
            : !survey.staticSurvey && (
                <div key={index} className="chat right-chat">
                    <div className="chat-img user--icon">
                        <i className="fal fa-user"></i>{' '}
                    </div>

                    <div className={classNames('chat-bubble')} message-id={item.message_id}>
                        <div className="chat-text">
                            <p> {item.text.join(',')} </p>
                        </div>
                        {clientMessages &&
                            clientMessages.length > 0 &&
                            !survey.staticSurvey &&
                            clientMessages[clientMessages.length - 1].question_order === item.question_order && (
                                <button
                                    type="button"
                                    className={classNames('pills-btn')}
                                    disabled={loading}
                                    onClick={() => HandleResponse([], -1, null, false, true, '')}>
                                    <Tooltipster
                                        className="sentiment-link"
                                        side="top"
                                        theme="tooltipster-light"
                                        content={<p style={{ whiteSpace: 'pre-line' }}>{t('UNDO')}</p>}></Tooltipster>
                                    undo <i className="fal fa-rotate-left"></i>
                                </button>
                            )}
                    </div>
                </div>
            );
    });

    let disabled =
        (convo && convo.length > 0 && convo[convo.length - 1]?.question_type !== 'Comment List') ||
        loading ||
        total_question === 0;

    return (
        <React.Fragment>
            {(status === 'Completed' || status === 'SurveyCompleted') && <ThankYou />}
            {(status === 'Expired' || status === 'SurveyExpired') && <Expired />}
            {status === 'InValid' && <InvalidSession />}
            {status !== 'Completed' &&
                status !== 'SurveyCompleted' &&
                status !== 'SurveyExpired' &&
                status !== 'Unsubscribed' &&
                status !== 'InValid' &&
                status !== 'Expired' && (
                    <div
                        className={classNames('update-loader', {
                            loading: loading && survey.staticSurvey,
                            'no-loading': !loading
                        })}>
                        <div className="wrapper">
                            {showProgress && (
                                <div>
                                    <div>
                                        <Tooltipster
                                            className="sentiment-link"
                                            side="top"
                                            placement="top-right"
                                            theme="tooltipster-light"
                                            content={
                                                <p style={{ whiteSpace: 'pre-line' }}>
                                                    {done} /{total} ({progress.toFixed(2)}%)
                                                </p>
                                            }>
                                            {/* <Tooltip target=".p-progressbar-value" position="top" at="top right" content={`${question_done.length - 1}/${total} (${progress.toFixed(2)}%)`} /> */}
                                        </Tooltipster>
                                    </div>
                                </div>
                            )}
                            <div className="container">
                                <main className="main">
                                    <div className="content">
                                        <div className="chat-container">
                                            {renderHTML}
                                            {loading && !survey.staticSurvey && (
                                                <div className="chat left-chat">
                                                    <div className={'chat-img'}>
                                                        {!botIconPath && <BotIcon />}
                                                        {botIconPath && (
                                                            <img src={botIconPath} className="logo" alt="" />
                                                        )}
                                                    </div>
                                                    <div className="chat-bubble">
                                                        <div className="chat-text">
                                                            <p>
                                                                {/* {!survey.staticSurvey && (
                                                                    <img src={loader} alt={t('LOADING')} />
                                                                )} */}{' '}
                                                                <img src={loader} alt={t('LOADING')} />{' '}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div ref={messagesEndRef} />
                                    </div>
                                </main>
                            </div>
                        </div>
                    </div>
                )}
        </React.Fragment>
    );
}
